import {
    isNotableChange,
    NOTABLE_CHANGE_MIN_SHARE,
    scoreBreakdownChange,
    scoreSiteMetricChange,
} from "app/components/insight-ribbon/significance";
import type { INotableChange, INotableChangeDeepLink } from "app/components/insight-ribbon/types";
import { simplePercentageFilter } from "app/filters/ngFilters";
import { TotalTrafficSourceDisplayMap } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewBarChart/types";
import type { ICountryTrafficShare } from "app/pages/website-performance/components/TopCountries/type";
import type { IChannelVisitsPop } from "app/pages/website-performance/insight-ribbon/useTrafficChannelsPopData";
import CountryService from "app/services/CountryService";

const CONVERSION_RATE_DECIMALS = 1;

const relativeChange = (current: number, previous: number): number | null =>
    previous > 0 ? current / previous - 1 : null;

const sumVisits = (visitsByChannel: Record<string, number | null>): number =>
    Object.values(visitsByChannel).reduce<number>((sum, visits) => sum + (visits ?? 0), 0);

export const toChannelNotableChanges = (
    channels: IChannelVisitsPop | null | undefined,
    deepLink: INotableChangeDeepLink,
): INotableChange[] => {
    if (!channels) {
        return [];
    }

    const currentTotal = sumVisits(channels.current);
    const comparedTotal = sumVisits(channels.compared);

    if (currentTotal <= 0 || comparedTotal <= 0) {
        return [];
    }

    const channelNames = new Set([...Object.keys(channels.current), ...Object.keys(channels.compared)]);

    return [...channelNames].flatMap((channel) => {
        const visits = channels.current[channel] ?? 0;
        const comparedVisits = channels.compared[channel] ?? 0;
        // The larger of both months' shares, so a channel that collapses is judged by the traffic it lost.
        const share = Math.max(visits / currentTotal, comparedVisits / comparedTotal);
        const changeRatio = relativeChange(visits, comparedVisits);
        const label = TotalTrafficSourceDisplayMap[channel]?.text;

        if (!label || share < NOTABLE_CHANGE_MIN_SHARE || !isNotableChange(changeRatio)) {
            return [];
        }

        return {
            id: `channel-${channel}`,
            kind: "channel" as const,
            label,
            changeRatio,
            significance: scoreBreakdownChange(changeRatio, share),
            deepLink,
        };
    });
};

export const toGeoNotableChanges = (
    countries: ICountryTrafficShare[] | undefined,
    deepLink: INotableChangeDeepLink,
): INotableChange[] =>
    (countries ?? []).flatMap((country) => {
        const label = CountryService.getCountryById(country.country)?.text;

        if (!label || country.share < NOTABLE_CHANGE_MIN_SHARE || !isNotableChange(country.change)) {
            return [];
        }

        return {
            id: `geo-${country.country}`,
            kind: "geo" as const,
            label,
            changeRatio: country.change,
            significance: scoreBreakdownChange(country.change, country.share),
            countryCode: country.country,
            deepLink,
        };
    });

/**
 * Conversion rate reports its delta in percentage points, so it is displayed as `pp` but ranked on the
 * relative move against the implied previous month — otherwise it would not be comparable with the
 * ratio-based channel and country candidates.
 */
export const toConversionRateNotableChange = (
    conversionRate: number | null | undefined,
    changeInPoints: number | null | undefined,
    label: string,
    deepLink: INotableChangeDeepLink,
): INotableChange[] => {
    if (conversionRate == null || changeInPoints == null) {
        return [];
    }

    const changeRatio = relativeChange(conversionRate, conversionRate - changeInPoints);

    if (!isNotableChange(changeRatio)) {
        return [];
    }

    return [
        {
            id: "conversion-rate",
            kind: "conversion" as const,
            label,
            changeRatio,
            formattedChange: `${simplePercentageFilter()(Math.abs(changeInPoints), CONVERSION_RATE_DECIMALS)}pp`,
            significance: scoreSiteMetricChange(changeRatio),
            deepLink,
        },
    ];
};
